import React, { useState, useEffect } from "react";
import { Modal, Button, Input, Space } from "antd";
import { SearchOutlined } from "@ant-design/icons";

import { fetchSamples } from "../../../../../api/labiq/SampleApi";
import { createAuthSamples } from "../../../../../api/geneCodes/AuthSamples";

// Types
import { Patient } from "../../../../../types/healthcare/PatientTypes";
import { DataTable } from "../../../../../components/shared";

interface AssignSampleModalProps {
	isOpen: boolean;
	onClose: () => void;
	patient: Patient;
	handleErrors: (error: unknown) => void;
	fetchPatientSample: () => void;
}

const AssignSampleModal: React.FC<AssignSampleModalProps> = ({ isOpen, onClose, patient, handleErrors, fetchPatientSample }) => {
	const [searchTerm, setSearchTerm] = useState("");
	const [searchResults, setSearchResults] = useState<any[]>([]);
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		if (isOpen) {
			searchSamples("");
		}
	}, [isOpen]);

	const assignSample = async (sampleClientId: string) => {
		try {
			await createAuthSamples(patient.email, sampleClientId);
			await fetchPatientSample();
			onClose();
		} catch (error: unknown) {
			handleErrors(error);
		}
	};

	const searchSamples = async (term: string) => {
		setLoading(true);
		try {
			const { data } = await fetchSamples(1, 20, "sampleID", "desc", term);
			setSearchResults(data);
		} catch (error: unknown) {
			handleErrors(error);
		} finally {
			setLoading(false);
		}
	};

	const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const term = e.target.value;
		setSearchTerm(term);
		if (term) {
			searchSamples(term);
		} else {
			setSearchResults([]);
		}
	};

	const columns = [
		{
			title: "Sample ID",
			dataIndex: "sampleID",
			key: "sampleID",
		},
		{
			title: "Date",
			key: "date",
			render: (record: any) => new Date(record.created_at).toLocaleDateString("en-GB"),
		},
		{
			title: "Status",
			key: "status",
			render: (record: any) => record.sampleOA?.status || "N/A",
		},
	];

	return (
		<Modal
			title="Assign Test To Patient"
			open={isOpen}
			onCancel={onClose}
			footer={[
				<Button key="cancel" onClick={onClose}>
					Cancel
				</Button>,
			]}
			width={800}
			destroyOnClose
		>
			<Space direction="vertical" style={{ width: "100%" }} size="large">
				<Input placeholder="Search existing samples..." prefix={<SearchOutlined />} value={searchTerm} onChange={handleSearchChange} allowClear />

				{searchResults.length > 0 && (
					<DataTable
						columns={columns}
						dataSource={searchResults}
						rowKey="sampleID"
						loading={loading}
						pagination={false}
						onRowClick={(record) => {
							setSearchTerm("");
							setSearchResults([]);
							assignSample(record.sampleID);
						}}
						emptyTitle="No samples found"
					/>
				)}
			</Space>
		</Modal>
	);
};

export default AssignSampleModal;
